/**
 * NAMELESS · Movimiento (10/10/2026)
 * - Aparición en cascada al hacer scroll (fundido hacia arriba) de secciones, tarjetas y pie.
 * - Fundido de las fotos de producto al terminar de descargarse.
 * Solo actúa si <html> tiene la clase nl-motion (no se pone en el editor del tema).
 */
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('nl-motion')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  // Unidades que aparecen una por una (en cascada dentro de su grupo)
  const ITEM_SELECTOR = [
    '.collection-product-grid > .product-card',
    '.multi-carousel-grid > .carousel-column',
    '.product-grid > li',
    'product-recommendations .resource-list__item',
    '.resource-list > *',
    '.footer-content > *',
  ].join(',');

  // Secciones que aparecen enteras (si no contienen unidades propias)
  const SECTION_SELECTOR = '#MainContent > .shopify-section';

  const STAGGER_MS = 90;
  const MAX_DELAY_MS = 450;

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      visible.forEach((entry, i) => {
        const el = entry.target;
        el.style.setProperty('--nl-delay', `${Math.min(i * STAGGER_MS, MAX_DELAY_MS)}ms`);
        el.classList.add('nl-in');
        observer.unobserve(el);
        // Al terminar se limpia el transform para no interferir con efectos propios del tema
        el.addEventListener(
          'transitionend',
          () => {
            el.classList.remove('nl-reveal', 'nl-in');
            el.style.removeProperty('--nl-delay');
          },
          { once: true }
        );
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  /** Prepara un elemento: solo se oculta si está por debajo de lo que se ve al cargar. */
  const prepare = (el) => {
    if (el.dataset.nlMotion) return;
    el.dataset.nlMotion = '1';
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92 || rect.height === 0) return; // ya visible: lo anima el fundido de carga
    el.classList.add('nl-reveal');
    observer.observe(el);
  };

  /** Fundido de imágenes de producto que todavía se están descargando. */
  const prepareImage = (img) => {
    if (img.dataset.nlImg) return;
    img.dataset.nlImg = '1';
    if (img.complete && img.naturalWidth > 0) return;
    img.classList.add('nl-img-fade', 'nl-img-wait');
    const done = () => img.classList.remove('nl-img-wait');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  };

  const IMG_SELECTOR = '.product-card img, .carousel-item img, .resource-card__image, .card-gallery img';

  const scan = (scope = document) => {
    const items = scope.querySelectorAll(ITEM_SELECTOR);
    items.forEach(prepare);
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (!section.querySelector(ITEM_SELECTOR)) prepare(section);
    });
    scope.querySelectorAll(IMG_SELECTOR).forEach(prepareImage);
  };

  const start = () => {
    scan();
    // Contenido que llega después (recomendaciones, filtros, "cargar más")
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(ITEM_SELECTOR)) prepare(node);
          if (node.matches?.(IMG_SELECTOR)) prepareImage(node);
          scan(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
